import React, { useRef, useState } from 'react';
import ReactCrop, { Crop } from 'react-image-crop';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import 'react-image-crop/dist/ReactCrop.css';

const UploadImageDialog = observer(
    ({
        store,
        width = 512,
        height = 512,
        aspectRatio = 1,
    }: {
        store: any;
        width?: number;
        height?: number;
        aspectRatio?: number;
    }) => {
        const [crop, setCrop] = useState<Crop>({
            unit: 'px', // Use pixels for the crop area
            width, // Default width of the crop area
            height, // Default height of the crop area
            x: 0, // Default x position
            y: 0, // Default y position
        });
        const [cropDialogVisible, setCropDialogVisible] = useState(false);
        const [imageSrc, setImageSrc] = useState<string | null>(null);
        const [croppedImage, setCroppedImage] = useState<string | null>(null);
        const imageRef = useRef<HTMLImageElement | null>(null);
        const fileInputRef = useRef<HTMLInputElement | null>(null);
        const useImage = croppedImage || store.imageBlob;

        const handleImageLoaded = () => {
            if (imageRef.current) {
                const image = imageRef.current;

                // Get the rendered size of the image
                const renderedWidth = image.width;
                const renderedHeight = image.height;

                // Calculate the default crop size based on aspect ratio
                let cropWidth = width;
                let cropHeight = height;

                // Begrenze das Aspect Ratio auf maximal 200/40 (5:1)
                const maxAspectRatio = 200 / 40;
                const effectiveAspectRatio = Math.min(
                    aspectRatio || width / height,
                    maxAspectRatio
                );

                // Berechne Crop-Dimensionen: Höhe ist führend, Breite wird angepasst
                // Verwende die gewünschte Breite, aber respektiere das Aspect Ratio
                const desiredWidth = Math.min(width, height * effectiveAspectRatio);
                const desiredHeight = height;

                // Skaliere basierend auf verfügbarem Platz im Bild
                const availableWidth = renderedWidth;
                const availableHeight = renderedHeight;

                // Berechne Skalierungsfaktor basierend auf dem, was ins Bild passt
                const scaleByWidth = availableWidth / desiredWidth;
                const scaleByHeight = availableHeight / desiredHeight;
                const scale = Math.min(scaleByWidth, scaleByHeight);

                cropWidth = desiredWidth * scale;
                cropHeight = desiredHeight * scale;

                // Stelle sicher, dass Crop nicht größer als das Bild ist
                cropWidth = Math.min(cropWidth, renderedWidth);
                cropHeight = Math.min(cropHeight, renderedHeight);

                const x = (renderedWidth - cropWidth) / 2; // Center horizontally
                const y = (renderedHeight - cropHeight) / 2; // Center vertically

                setCrop({
                    unit: 'px',
                    width: cropWidth,
                    height: cropHeight,
                    x,
                    y,
                });
            }
        };

        const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.target.files && e.target.files.length > 0) {
                const reader = new FileReader();
                reader.onload = () => setImageSrc(reader.result as string);
                reader.readAsDataURL(e.target.files[0]);
                setCropDialogVisible(true);

                store.setImage(e.target.files[0]);
            }
        };

        const handleCropComplete = (crop: Crop) => {
            if (imageSrc && crop.width && crop.height && imageRef.current) {
                const canvas = document.createElement('canvas');
                const image = imageRef.current;

                // Berechne das Skalierungsverhältnis
                const scaleX = image.naturalWidth / image.width;
                const scaleY = image.naturalHeight / image.height;

                // Originalgröße des zugeschnittenen Bereichs
                const croppedWidth = crop.width * scaleX;
                const croppedHeight = crop.height * scaleY;

                // Zielgröße basierend auf dem tatsächlichen Crop
                const cropAspectRatio = croppedWidth / croppedHeight;
                const maxAspectRatio = 200 / 40; // 5:1 maximales Verhältnis

                // Immer Höhe von height parameter, Breite basierend auf dem tatsächlichen Crop-Verhältnis
                let targetHeight = height; // Feste Höhe
                let targetWidth = height * cropAspectRatio; // Breite basierend auf tatsächlichem Crop-Verhältnis

                // Begrenze die Breite falls nötig
                if (cropAspectRatio > maxAspectRatio) {
                    targetWidth = height * maxAspectRatio;
                }

                // Begrenze auch durch den width Parameter
                if (targetWidth > width) {
                    targetWidth = width;
                    // Höhe könnte sich ändern, um das Verhältnis zu erhalten
                    targetHeight = width / cropAspectRatio;
                }

                // Setze die Zielgröße des Canvas
                canvas.width = targetWidth;
                canvas.height = targetHeight;

                const ctx = canvas.getContext('2d');
                if (ctx) {
                    // Zeichne das zugeschnittene Bild auf das Canvas
                    ctx.drawImage(
                        image,
                        crop.x * scaleX,
                        crop.y * scaleY,
                        croppedWidth,
                        croppedHeight,
                        0,
                        0,
                        targetWidth,
                        targetHeight
                    );

                    // Konvertiere das Canvas in ein Blob
                    canvas.toBlob(
                        blob => {
                            if (blob) {
                                const croppedImageUrl = URL.createObjectURL(blob);
                                setCroppedImage(croppedImageUrl); // Setze die Vorschau des zugeschnittenen Bildes
                                store.setImage(blob); // Speichere das Blob im Store
                                setCropDialogVisible(false); // Schließe den Dialog
                            }
                        },
                        'image/jpeg',
                        0.8
                    ); // Optional: Qualität auf 90% setzen
                }
            }
        };

        return (
            <>
                <div style={{ marginTop: '1rem', marginBottom: '1rem', textAlign: 'center' }}>
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                    />
                    {useImage ? (
                        <img
                            src={useImage}
                            alt="Cropped"
                            style={{
                                width: `${Math.min(150, width * 0.3)}px`,
                                height: `${Math.min(150, height * 0.3)}px`,
                                objectFit: 'cover',
                                cursor: 'pointer',
                                borderRadius: '8px',
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        />
                    ) : (
                        <div>
                            <Button
                                icon="pi pi-upload"
                                className="p-button-rounded p-button-secondary"
                                onClick={() => fileInputRef.current?.click()}
                                style={{ width: '4rem', height: '4rem' }}
                            />
                            <div
                                style={{
                                    marginTop: '0.5rem',
                                    fontSize: '0.9rem',
                                    color: '#6c757d',
                                }}
                            >
                                Bild hinzufügen
                            </div>
                        </div>
                    )}
                </div>
                <Dialog
                    visible={cropDialogVisible}
                    modal
                    header={'Bild zuschneiden'}
                    footer={
                        <Button
                            label="Speichern"
                            onClick={() => {
                                if (crop) {
                                    handleCropComplete(crop);
                                }
                            }}
                        />
                    }
                    onHide={() => setCropDialogVisible(false)}
                >
                    {imageSrc && (
                        <ReactCrop
                            crop={crop}
                            onChange={(newCrop: any) => {
                                // Erlaube freie Anpassung der Breite, aber begrenze das Aspect Ratio
                                const currentAspectRatio = newCrop.width / newCrop.height;
                                const maxAspectRatio = 200 / 40; // 5:1

                                if (currentAspectRatio > maxAspectRatio) {
                                    // Wenn zu breit, reduziere die Breite
                                    newCrop.width = newCrop.height * maxAspectRatio;
                                }

                                setCrop(newCrop);
                            }}
                            // Entferne aspect property für freie Anpassung
                        >
                            <img
                                ref={imageRef}
                                src={imageSrc}
                                alt={'cropped-image'}
                                style={{ maxWidth: '512px' }}
                                onLoad={handleImageLoaded} // Set default crop when the image is loaded
                            />
                        </ReactCrop>
                    )}
                </Dialog>
            </>
        );
    }
);

export default UploadImageDialog;
